// @ai-generated 2026-08-10T06:52:00+00:00 by niklas.eicker@tngtech.com using Claude Opus 5
import { type ApiLocationSnapshot, type ApiRequisition, type ApiService } from '@Model';
import { type LinkableField, renderDateTime } from '@synqo/frontend-component-library';
import { materialsInPlaceOfPositions } from './containerMaterials';

export interface SiteLines {
    /** Name of the site, e.g. "Baustelle Hafenstraße". Empty when the address carries no label. */
    name: string;

    /** Street and city of the site, e.g. "Hafenstraße 12, 20359 Hamburg". */
    address: string;
}

/**
 * Splits a site into the two lines the portal shows: its name and its postal address. Falls back to
 * the address line as the name when the address has no label, so a site is never rendered nameless.
 */
export function getSiteLines(site: ApiLocationSnapshot | undefined): SiteLines {
    const address = site?.address;
    if (!address) {
        return { name: '', address: '' };
    }

    const street = [address.street, address.house_number].filter(Boolean).join(' ');
    const city = [address.zip_code, address.city].filter(Boolean).join(' ');
    const addressLine = [street, city].filter(Boolean).join(', ');

    if (address.label) {
        return { name: address.label, address: addressLine };
    }
    return { name: addressLine || address.display_string, address: '' };
}

/**
 * What a material is called in this session. The customer gets the name the operator picked for the
 * portal where there is one; the ERP keeps its own.
 * @ai-generated 2026-09-09T07:53:29+00:00 by niklas.eicker@tngtech.com using Claude Opus 5
 */
export function formatMaterialName(service: ApiService, isPortalSession: boolean): string {
    if (isPortalSession && service.external_label) {
        return service.external_label;
    }
    return service.display_string;
}

/**
 * The services a requisition shows as its material: its positions, or, while it has none at all, the
 * materials of its container actions.
 */
// @ai-generated 2026-09-30T11:45:02+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function requisitionMaterials(
    requisition: Pick<ApiRequisition, 'positions' | 'container_actions'>,
): LinkableField[] {
    const positions = requisition.positions ?? [];

    return [
        ...positions.map((position) => position.service),
        ...materialsInPlaceOfPositions(positions, requisition.container_actions ?? []),
    ];
}

/**
 * The requested materials of a requisition as one line, e.g. "Bauschutt, Altholz A1–A3". The
 * ordered quantity stays out of it: it belongs to the position, not to the material.
 */
export function formatRequisitionMaterial(
    requisition: Pick<ApiRequisition, 'positions' | 'container_actions'>,
    labelOf: (service: LinkableField | undefined) => string,
): string {
    const materials = requisitionMaterials(requisition)
        .map((service) => labelOf(service))
        .filter(Boolean);

    return [...new Set(materials)].join(', ');
}

/**
 * The requested date of a requisition: one day, a range when the customer named a latest date, and
 * "Schnellstmöglich" without one. Shared by list and detail so both report the same wish.
 * @ai-generated 2026-08-12T00:00:00+00:00 by niklas.eicker@tngtech.com using Claude Opus 5
 */
export function formatRequisitionDesiredDate(requisition: Pick<ApiRequisition, 'date_from' | 'date_to'>): string {
    const from = requisition.date_from ? renderDateTime(requisition.date_from, 'date') : undefined;
    const to = requisition.date_to ? renderDateTime(requisition.date_to, 'date') : undefined;

    if (!from) {
        return 'Schnellstmöglich';
    }
    if (!to || to === from) {
        return from;
    }
    return `${from} – ${to}`;
}
